import clsx from 'clsx';
import * as React from 'react';
import { usePress } from 'react-aria';

import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    KeyboardShortcut,
    LoadingIndicator,
    renderIcon,
    Skeleton,
    Switch,
    Tooltip,
} from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { sizes, typography } from '../../constants';
// Prevents a circular dependency
import { CheckBox } from '../../Form/CheckBox';
import { Radio } from '../../Form/Radio';
import { TextMatchHighlight } from '../../Texts';
import { type PaletteCommandDef, PaletteCommandType } from '../types';
import { resolveSubtitle } from './util';

type PaletteCommandItemProps = {
    command: PaletteCommandDef;
    query: string;
    className?: string;
    /** True is passed when the command is active in the list. */
    active: boolean;
    /** Callback to execute the command. */
    executeCommand: (command: PaletteCommandDef) => Promise<void> | void;
    /** Callback when the command is hovered. */
    onHoverIn: (command: PaletteCommandDef) => void;
    /** If true, only the icon will be displayed and the tooltip should show on hover. */
    iconOnly?: boolean;
};

/**
 * Item for a command in the palette.
 */
export const PaletteCommandItem = React.forwardRef(function PaletteCommandItem(
    props: PaletteCommandItemProps,
    ref: React.Ref<HTMLDivElement>
): React.ReactElement {
    const { command, query, active, executeCommand, onHoverIn, iconOnly = false } = props;

    const [loading, wrapLoading] = useLoadingState(false);

    function executeThisCommand() {
        const result = executeCommand(command);
        if (result instanceof Promise) {
            wrapLoading(() => result);
        }
    }

    const isIconOnly = iconOnly && Boolean(command.icon);
    const IconCommand = command.icon;

    const subtitle = resolveSubtitle(command);
    const { pressProps } = usePress({
        isDisabled: command.disabled,
        onPress: executeThisCommand,
    });

    return (
        <Tooltip label={command.title} disabled={!isIconOnly}>
            <div
                ref={ref}
                {...pressProps}
                onMouseEnter={() => onHoverIn(command)}
                onFocus={() => onHoverIn(command)}
                // @ts-ignore - onMouseDown will be passed as an event handler
                // We need to prevent the default action to avoid blurring the input (when using palette as an autocomplete)
                // The downside is that text is not selectable in this item because of it.
                onMouseDown={(event) => {
                    event.preventDefault();
                }}
                className={clsx(
                    'mx-1 my-px flex select-none items-center justify-between gap-2 rounded px-2 py-1.5 font-normal',
                    active ? 'cursor-pointer' : 'cursor-default',
                    isIconOnly && command.selected
                        ? 'bg-action-active'
                        : active
                          ? command.upgrade
                              ? 'bg-upgrade'
                              : 'bg-standout-base'
                          : 'bg-base',
                    (command.disabled || loading) &&
                        'pointer-events-none cursor-default opacity-50',
                    isIconOnly && 'size-7.5',
                    props.className
                )}
                style={{
                    // Enforce height of the view so that list can be virtualized without issue
                    height: getPaletteCommandItemHeight(command),
                }}
                data-testid={command.testID}
            >
                {loading ? (
                    <LoadingIndicator size="medium" />
                ) : IconCommand ? (
                    <>
                        {/* things like emojis often suspend rendering, ensure its handled here */}
                        <React.Suspense fallback={<Skeleton className="size-4 rounded" />}>
                            {renderIcon(IconCommand, {
                                size: 16,
                                className: clsx(
                                    isIconOnly && command.selected
                                        ? 'text-action-active'
                                        : active
                                          ? command.upgrade
                                              ? 'text-upgrade'
                                              : 'text-base'
                                          : 'text-muted opacity-95'
                                ),
                            })}
                        </React.Suspense>
                    </>
                ) : null}

                {isIconOnly ? null : (
                    <>
                        <div className="flex-1">
                            <div className="flex-1">
                                {command.title ? (
                                    <div className="flex items-center justify-between">
                                        <div className="truncate font-normal text-ui-action">
                                            <TextMatchHighlight
                                                query={query}
                                                text={command.title}
                                                highlightClassName="font-bold"
                                            />
                                        </div>
                                        {command.badge ? (
                                            <div className="shrink-0 pl-2">
                                                <Badge data-nudge-icon {...command.badge} />
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                                {subtitle ? (
                                    <div className="truncate text-muted text-ui-small">
                                        <TextMatchHighlight
                                            query={query}
                                            text={subtitle}
                                            highlightClassName="font-bold"
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        {/* For now we don't allow a shortcut _and_ a switch/checkbox/radio simply cause it'd need design work */}
                        {!command.type &&
                        'keyboardShortcut' in command &&
                        command.keyboardShortcut ? (
                            <div className="ml-4">
                                <KeyboardShortcut
                                    size="xsmall"
                                    shortcut={command.keyboardShortcut}
                                />
                            </div>
                        ) : null}
                        {command.descendant ? (
                            <div className="ml-4">
                                <Icon.ChevronRight size={18} />
                            </div>
                        ) : null}
                        {command.type === PaletteCommandType.CHECKBOX ? (
                            <div className="ml-4">
                                <CheckBox
                                    value={command.selected}
                                    onValueChange={executeThisCommand}
                                    disabled={command.disabled}
                                    size={sizes.ICON_400}
                                />
                            </div>
                        ) : null}
                        {command.type === PaletteCommandType.RADIO ? (
                            <div className="ml-4">
                                <Radio
                                    value={command.selected}
                                    onValueChange={executeThisCommand}
                                    disabled={command.disabled}
                                    size={16}
                                />
                            </div>
                        ) : null}
                        {command.type === PaletteCommandType.SWITCH ? (
                            <div className="ml-4">
                                <Switch
                                    value={command.enabled}
                                    onValueChange={() => {
                                        // Nothing to do, the click will be propagated to the parent element
                                    }}
                                    disabled={command.disabled}
                                    testID={command.testID}
                                />
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </Tooltip>
    );
});

/**
 * Get the height of a command item depending on its definition and the layout.
 * This method is also used to optimize virtual list rendering.
 */
export function getPaletteCommandItemHeight(command: PaletteCommandDef): number {
    let height = 0;

    // Vertical padding
    height += 12;

    // Title

    height += Math.max(
        command.title ? typography.Label.lineHeight || 0 : 0,
        command.icon ? sizes.ICON_400 : 0,
        typeof command.selected === 'boolean' || typeof command.enabled === 'boolean'
            ? sizes.ICON_400
            : 0
    );

    if (command.subtitle) {
        height += typography.Caption.lineHeight || 0;
    }

    return height;
}
